import { useSession } from "next-auth/react";
import { useSearchParams } from "next/navigation";
import { useEffect, useMemo } from "react";
import { AnalyticsEvents, trackEvent } from "~/connectors/analytics.connector";
import { generateUUID } from "~/lib/generateUUID";


export const TRACKING_ID_KEY = "user_tracking_id";
export const TRACKING_ID_PARAM = "tid";

// Track if first-seen event has been fired in this browser session
// This persists across component remounts but resets on page reload

/**
 * Hook to manage user tracking ID
 * Generates and persists a UUID for analytics tracking from first visit
 * Also reads tracking ID from URL query params (tid) for redirects
 */
export function useTrackingId() {
  const { data: session, status } = useSession();
  const searchParams = useSearchParams();
  const sessionUserTrackingId =
    session?.user?.userTrackingId || session?.user?.id || null;

  const trackingId = useMemo(() => {
    if (status === "loading") return null;

    if (sessionUserTrackingId) {
      return sessionUserTrackingId;
    }

    // Check URL query params first (for redirects)
    let id = searchParams.get(TRACKING_ID_PARAM);

    if (!id) {
      // Check if tracking ID exists in localStorage
      id = localStorage.getItem(TRACKING_ID_KEY);
    }

    if (!id) {
      id = generateUUID();

      // Track first-seen event when generating a new tracking ID
      // This happens before any other event including page view
      trackEvent(AnalyticsEvents.FIRST_SEEN);
    }

    return id;
  }, [searchParams, status, sessionUserTrackingId]);

  useEffect(() => {
    if (status === "loading" || !trackingId) return;

    // Clean up URL by removing the tracking ID param if it was present
    if (searchParams.get(TRACKING_ID_PARAM)) {
      const url = new URL(window.location.href);
      url.searchParams.delete(TRACKING_ID_PARAM);
      window.history.replaceState({}, "", url.toString());
    }

    // Store in localStorage and cookie
    localStorage.setItem(TRACKING_ID_KEY, trackingId);

    const secure = location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${TRACKING_ID_KEY}=${trackingId}; path=/; max-age=31536000; SameSite=Lax${secure}`;
  }, [searchParams, status, trackingId]);

  return trackingId;
}
